import { apiClient } from '../http/query-client';

const FBCLID_KEY = 'sb_fbclid';
const FBC_KEY = 'sb_fbc';
const FBCLID_TS_KEY = 'sb_fbclid_ts';

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + escapedName + '=([^;]*)'));
    if (!match) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function setCookie(name: string, value: string, days: number = 90): void {
  if (typeof document === 'undefined') return;
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const isSpacebyte = typeof window !== 'undefined' && window.location.hostname.includes('spacebyte.in');
    const domain = isSpacebyte ? '; domain=.spacebyte.in' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domain}; SameSite=Lax`;
  } catch {
    // Cookie write failure should never break checkout
  }
}

/**
 * Clean and normalize fbc string to guarantee Meta's required 13-digit millisecond timestamp
 */
function normalizeFbc(fbc: string): string {
  try {
    const parts = fbc.split('.');
    if (parts.length >= 4 && parts[2].length === 10) {
      // Auto-heal legacy 10-digit seconds timestamp into 13-digit milliseconds
      parts[2] = parts[2] + '000';
      return parts.join('.');
    }
  } catch {}
  return fbc;
}

export function saveFbclidFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    if (!fbclid) return;

    const existingFbclid = localStorage.getItem(FBCLID_KEY);
    const existingFbc = localStorage.getItem(FBC_KEY);

    // If already saved with valid 13-digit format, preserve original click timestamp
    if (existingFbclid === fbclid && existingFbc && existingFbc.startsWith('fb.1.')) {
      return;
    }

    // Meta Standard Format: fb.1.<creationTimeInMilliseconds>.<fbclid> (13-digit timestamp)
    const clickTimestampMs = Date.now().toString();
    const fbcValue = `fb.1.${clickTimestampMs}.${fbclid}`;

    localStorage.setItem(FBCLID_KEY, fbclid);
    localStorage.setItem(FBCLID_TS_KEY, clickTimestampMs);
    localStorage.setItem(FBC_KEY, fbcValue);

    // Write first-party cookie so native Meta Pixel (fbevents.js) reads it directly
    setCookie('_fbc', fbcValue, 90);
  } catch (e) {}
}

export function getFbc(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    // 1. Native Meta Pixel cookie
    const cookieVal = getCookie('_fbc');
    if (cookieVal && cookieVal.startsWith('fb.')) {
      return normalizeFbc(cookieVal);
    }

    // 2. Stored fbc from landing
    const storedFbc = localStorage.getItem(FBC_KEY);
    if (storedFbc && storedFbc.startsWith('fb.')) {
      return normalizeFbc(storedFbc);
    }

    // 3. Fallback to saved fbclid in localStorage with 13-digit millisecond timestamp
    const savedFbclid = localStorage.getItem(FBCLID_KEY);
    if (savedFbclid) {
      const clickTimeMs = localStorage.getItem(FBCLID_TS_KEY) || Date.now().toString();
      const fbcValue = `fb.1.${clickTimeMs}.${savedFbclid}`;
      localStorage.setItem(FBC_KEY, fbcValue);
      setCookie('_fbc', fbcValue, 90);
      return fbcValue;
    }
  } catch (e) {}

  return null;
}

export function getFbp(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    // 1. Native Meta Pixel cookie
    const cookieVal = getCookie('_fbp');
    if (cookieVal && cookieVal.startsWith('fb.')) {
      return cookieVal;
    }

    // 2. LocalStorage fallback
    const lsFbp = localStorage.getItem('_fbp');
    if (lsFbp && lsFbp.startsWith('fb.')) {
      return lsFbp;
    }
  } catch (e) {}

  return null;
}

// Auto-capture fbclid upon module load in browser context
if (typeof window !== 'undefined') {
  saveFbclidFromUrl();
}

export function trackFacebookEvent(
  eventName: string,
  customData: Record<string, any> = {},
  userData: Record<string, any> = {}
) {
  const hasFbq = typeof window !== 'undefined' && (window as any).fbq;
  const eventId = eventName.toLowerCase() + '_' + Math.random().toString(36).substring(2, 15);

  const fbcVal = getFbc();
  const fbpVal = getFbp();

  const enrichedUserData = {
    ...userData,
  };
  if (fbcVal && !enrichedUserData.fbc) enrichedUserData.fbc = fbcVal;
  if (fbpVal && !enrichedUserData.fbp) enrichedUserData.fbp = fbpVal;

  // 1. Fire Browser Event (Meta Pixel)
  if (hasFbq) {
    (window as any).fbq('track', eventName, customData, { eventID: eventId });
  }

  // 2. Fire Server Event (Conversions API via our backend proxy)
  apiClient.post('facebook/event', {
    event_name: eventName,
    event_id: eventId,
    user_data: enrichedUserData,
    custom_data: customData,
    event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
  }).catch(() => {});
}
